import type { ReactElement, ReactNode } from "react";

import type { IToast } from "app/components/React/Toast/types";
import { translate } from "app/filters/i18nFilter";
import type {
    RevealAndExportToastParams,
    RevealExportService,
} from "app/pages/sales-intelligence/common-components/table-actions-panel/models/reveal-service/types";
import ManagementToast from "app/pages/sales-intelligence/modules/crm-export/components/Toast/Toast";
import { createPluralHelperForI18nKey } from "app/pages/sales-intelligence/utils/strings";

function getToastLines(params: RevealAndExportToastParams): { line1: string; line2?: string } {
    const { revealedCount, unrevealedCount, requestedCount, exportedCount, isFullSuccess } = params;

    if (isFullSuccess) {
        return {
            line1: translate("si.contacts_search.export.combined.toast.success"),
        };
    }

    return {
        line1: translate("si.contacts_search.export.combined.toast.partial.line1", {
            revealedCount,
            unrevealedCount,
        }),
        line2: translate("si.contacts_search.export.combined.toast.partial.line2", {
            exportedCount,
            requestedCount,
        }),
    };
}

export const createRevealExportService = (): RevealExportService => {
    return {
        getToastComponent(text: string): ReactElement {
            return <ManagementToast text={text} />;
        },

        getSuccessToastTitle(numberOfRevealedContacts: number, numberOfTotalContacts: number): string {
            if (numberOfRevealedContacts === 1 && numberOfTotalContacts === 1) {
                return translate("si.contacts_search.action.reveal.toast.success.singular");
            }

            if (numberOfRevealedContacts !== numberOfTotalContacts) {
                const getTranslationKey = createPluralHelperForI18nKey(
                    "si.contacts_search.action.reveal.toast.success.partial",
                );

                return translate(getTranslationKey(numberOfRevealedContacts), {
                    numberOfRevealedContacts,
                    numberOfTotalContacts,
                });
            }

            return translate("si.table-actions-panel.reveal_export.success");
        },
        getToastTitle(quantity?: number): string {
            if (!quantity || quantity === 1) {
                return translate(`si.table-actions-panel.reveal_export.in_progress.one`, {
                    quantity,
                });
            }

            return translate(`si.table-actions-panel.reveal_export.in_progress`, {
                quantity,
            });
        },
        getErrorToastTitle(): string {
            return translate(`si.contacts_search.action.reveal.toast.failure`);
        },
        createToast(children: string): IToast {
            return {
                children,
                timestamp: Date.now(),
            };
        },

        getRevealAndExportToastLines(params: RevealAndExportToastParams): { line1: string; line2?: string } {
            return getToastLines(params);
        },

        getRevealAndExportToastElement(params: RevealAndExportToastParams): ReactNode {
            const { line1, line2 } = getToastLines(params);

            if (line2) {
                return (
                    <span>
                        {line1}
                        <br />
                        {line2}
                    </span>
                );
            }

            return line1;
        },
    };
};
